/* eslint-disable deprecation/deprecation,lumapps/do-not-import-react-query */
import React from 'react';

import * as rq from '@tanstack/react-query';

import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { getStatusFromQuery } from './utils';

export type QueriesResults<T extends any[]> = {
    /** Array of query results */
    queries: rq.QueriesResults<T>;
    /** Array of items (warning: items might be incomplete if queries haven't all resolved) */
    items?: Array<rq.QueriesResults<T>[number]['data']>;
    /** Combined queries status */
    baseLoadingStatus: BaseLoadingStatus;
    /** Some queries are still loading */
    isLoading: boolean;
    /** Some queries errored out */
    hasError: boolean;
};

/**
 * React Query Use Queries, used when doing API calls to services.
 * Please take a look at the examples on how to implement it.
 *
 * @example
 * import { useQueries } from '@lumapps/base-api/react-query';
 * import { instanceQueryKeys, getInstance } from '../../api';
 *
 * export const useInstances = (ids: string[]) =>
 *   useQueries({
 *      queries: ids.map((id) => ({
 *        queryKey: instanceQueryKeys.get(id),
 *        queryFn: ({ signal }) => getInstance(id, signal),
 *      })),
 *   });
 *
 * @param options
 * @returns QueriesResults
 */
export function useQueries<T extends any[]>(options: {
    queries: readonly [...rq.QueriesOptions<T>];
    context?: rq.UseQueryOptions['context'];
}): QueriesResults<T> {
    const queries = rq.useQueries(options);
    return React.useMemo(() => {
        const hasError = queries.some((query) => getStatusFromQuery(query) === BaseLoadingStatus.error);
        const isLoading = queries.some((query) => getStatusFromQuery(query) === BaseLoadingStatus.loading);

        let baseLoadingStatus: BaseLoadingStatus = BaseLoadingStatus.idle;
        if (hasError) {
            baseLoadingStatus = BaseLoadingStatus.error;
        } else if (isLoading) {
            baseLoadingStatus = BaseLoadingStatus.loading;
        }

        let items: any[] | undefined;
        if (options.queries.every((query) => query.enabled ?? true)) {
            items = queries.map((query) => query.data);
        }

        return { queries, items, baseLoadingStatus, isLoading, hasError };
    }, [options.queries, queries]);
}
